import { create } from "zustand";
import { checkTokenAuthStatus } from "@pages/LoginManagement/api.ts";
import type {
  BillingContext,
  Role,
  UserAuthData,
} from "@pages/LoginManagement/types/login.types.ts";
import { ifSkipAuth } from "@/constants.ts";
import { getTaxProInfo } from "@utils/api.ts";
import { shouldHideBillingSettings as computeShouldHideBillingSettings } from "@smartwiz/shared-types";
import type { BusinessFrontendMode } from "@smartwiz/shared-types";
import {
  mergeFrontendMigrationAuthFields,
  withFrontendMode,
} from "@/utils/frontendMigration";

// Global registry to track components that don't require auth
let noAuthComponents = new Set<string>();

/**
 * Register a component as not requiring authentication
 * @param componentId - Unique identifier for the component
 */
export const registerNoAuthComponent = (componentId: string) => {
  noAuthComponents.add(componentId);
};

/**
 * Unregister a component from the no-auth list
 * @param componentId - Unique identifier for the component
 */
export const unregisterNoAuthComponent = (componentId: string) => {
  noAuthComponents.delete(componentId);
};

/**
 * Check if any no-auth component is currently active
 * @returns True if any component has registered as no-auth
 */
export const hasNoAuthComponent = () => {
  return noAuthComponents.size > 0;
};

/**
 * Clear all registered no-auth components
 * Used to reset the registry in special cases like admin login flows
 */
export const clearNoAuthComponents = () => {
  noAuthComponents.clear();
};

/** Single-flight guard: concurrent refreshAuth callers share one in-flight request */
let inFlightRefresh: Promise<void> | null = null;

export interface BusinessInfo {
  businessName: string;
  packageName: string;
  logoUrl: string;
  firstName?: string;
  detailedFlow?: boolean; // Whether business uses detailed tax organizer flow
  businessId?: string;
  vanitySlug?: string; // Business-level slug used for Smart Intake invite URLs (m.smartwiz.io/i/<slug>)
}

export interface AuthStore {
  user: UserAuthData | null;
  /** From ``/api/auth/token/check`` (and login when present). */
  billingContext: BillingContext | null;
  loading: boolean;
  authCheckAttempted: boolean; // Track if we've tried to check auth (regardless of success/failure)
  refreshAuth: () => Promise<void>;
  setUser: (user: UserAuthData | null) => void;
  setAuthSession: (
    user: UserAuthData | null,
    billingContext?: BillingContext | null
  ) => void;
  checkAuthForPath: (pathname: string) => Promise<void>;

  // Role-related functionality (merged from userRoleStore)
  role?: Role;
  roleInitialized: boolean;
  setRole: (role: Role) => void;
  clearRole: () => void;
  isOwner: () => boolean;
  isTeamMember: () => boolean;
  isTeamLead: () => boolean;
  isTeamAdmin: () => boolean;

  // Authentication method
  getAuthMethod: () => "app" | "google" | undefined;

  // Business information
  businessInfo: BusinessInfo | null;
  fetchBusinessInfo: () => Promise<void>;

  /** True when the user has an active service bureau profile (see UserAuthData.isServiceBureau). */
  isServiceBureauUser: () => boolean;

  /** True when self-serve billing settings should be hidden (bureau pool/backed). */
  shouldHideBillingSettings: () => boolean;

  /** Apply a confirmed business frontend mode to the in-memory session user. */
  updateFrontendMode: (frontendMode: BusinessFrontendMode) => void;
}

export const useAuthStore = create<AuthStore>((set, get) => ({
  user: null,
  billingContext: null,
  loading: true,
  authCheckAttempted: false,

  // Role-related state (merged from userRoleStore)
  role: undefined,
  roleInitialized: false,

  // Business information state
  businessInfo: null,

  fetchBusinessInfo: async () => {
    try {
      const response = await getTaxProInfo();
      if (response?.data?.content) {
        const businessData: BusinessInfo = {
          businessName: response.data.content.businessName || "",
          packageName: response.data.content.packageName || "",
          logoUrl: response.data.content.logoUrl || "",
          firstName: response.data.content.firstName,
          detailedFlow: response.data.content.detailedFlow ?? true,
          businessId: response.data.content.businessId,
          vanitySlug: response.data.content.vanitySlug || undefined,
        };
        set({ businessInfo: businessData });

        // Store logoUrl in sessionStorage for backward compatibility
        if (businessData.logoUrl) {
          sessionStorage.setItem("logoUrl", businessData.logoUrl);
        }

        console.log("Business info retrieved:", businessData.businessName);
      }
    } catch (error) {
      console.error("Failed to fetch business info:", error);
      set({ businessInfo: null });
    }
  },

  refreshAuth: async () => {
    if (inFlightRefresh) {
      return inFlightRefresh;
    }
    inFlightRefresh = (async () => {
      console.log("[AuthRefresh] refreshAuth called");
      set({ loading: true });
      try {
        const response = await checkTokenAuthStatus();
        // mergeFrontendMigrationAuthFields: see frontendMigration.ts — only fills
        // omitted migration keys during refresh; explicit API values win.
        const userData = mergeFrontendMigrationAuthFields(
          response.content.user,
          get().user
        );
        set({
          user: userData,
          billingContext: response.content.billingContext ?? null,
          role: userData.role,
          roleInitialized: true,
        });
        console.log(`User has been retrieved: ${userData.email}`);

        // Fetch business info after user is set (only for non-client roles)
        if (userData?.role && userData.role !== "client") {
          await get().fetchBusinessInfo();
        }
      } catch (error) {
        console.error("Failed to retrieve user.");
        set({
          user: null,
          billingContext: null,
          role: undefined,
          roleInitialized: false,
          businessInfo: null,
        });
        throw error;
      } finally {
        set({ loading: false, authCheckAttempted: true });
        inFlightRefresh = null;
      }
    })();
    return inFlightRefresh;
  },

  setUser: (user) => {
    set({
      user,
      billingContext: null,
      role: user?.role,
      roleInitialized: !!user,
    });

    // Fetch business info after user is set (only for non-client roles)
    if (user?.role && user.role !== "client") {
      get().fetchBusinessInfo();
    }
  },

  setAuthSession: (user, billingContext = null) => {
    set({
      user,
      billingContext,
      role: user?.role,
      roleInitialized: !!user,
    });

    // Fetch business info after user is set (only for non-client roles)
    if (user?.role && user.role !== "client") {
      get().fetchBusinessInfo();
    }
  },

  // Role-related methods (merged from userRoleStore)
  setRole: (role: Role) => set({ role, roleInitialized: true }),

  clearRole: () => set({ role: undefined, roleInitialized: false }),

  isOwner: () => get().role === "owner",

  isTeamMember: () => get().role?.startsWith("team") || get().role === "owner",

  isTeamLead: () => get().role === "team_lead",

  isTeamAdmin: () => ["team_lead", "owner"].includes(get().role || ""),

  isServiceBureauUser: () => get().user?.isServiceBureau === true,

  shouldHideBillingSettings: () =>
    computeShouldHideBillingSettings(get().billingContext),

  updateFrontendMode: (frontendMode) => {
    const user = get().user;
    if (!user) {
      return;
    }
    set({ user: withFrontendMode(user, frontendMode) });
  },

  // Authentication method helper
  getAuthMethod: () => {
    const user = get().user;
    return user?.authMethod;
  },

  checkAuthForPath: async (pathname: string) => {
    // Check if we're on an auth path
    const shouldSkipAuth = ifSkipAuth(pathname);

    // Check for dynamic business slug routes (e.g., /somebusiness/login)
    const hasBusinessSlug =
      /^\/[^\/]+\/(login|register|forgotpassword|resetpassword\/confirm)$/.test(
        pathname
      );

    const noAuthComponentActive = hasNoAuthComponent();

    // If we're on an auth path, business slug route, or have no-auth components active, don't check auth
    if (shouldSkipAuth || hasBusinessSlug || noAuthComponentActive) {
      set({ loading: false });
    } else {
      await get().refreshAuth();
    }
  },
}));
